import {Box, useTheme} from "@mui/material";
import {darken, lighten} from "@mui/material/styles";

import * as React from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Label,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from "recharts";
import {NEGATIVE_COLOR, POSITIVE_COLOR, TARGET_COLOR} from "../../../insights/kit/insightsColors";
import {formatFullDate, formatLocalDate} from "../../../insights/kit/insightsFormat";
import {InsightsLegend, LegendEntry} from "../../../insights/kit/InsightsLegend";
import {InsightsTooltipPanel, TooltipTitle} from "../../../insights/kit/InsightsTooltip";
import {BAR_DRAW_DURATION, MARKER_FADE_DELAY, MARKER_FADE_DURATION} from "../constants";

interface Paint {
    stroke: string;
    strokeHover: string;
    fill: string;
}

const POSITIVE_PAINT: Paint = {stroke: POSITIVE_COLOR, strokeHover: "#066542", fill: "#96DFC3"};
const NEGATIVE_PAINT: Paint = {stroke: NEGATIVE_COLOR, strokeHover: "#98161C", fill: "#DF9699"};

const SERIES_WIDTH = 2.5;
const SERIES_WIDTH_HOVER = 3.5;
const COMPARE_WIDTH = 2;
const TARGET_WIDTH = 1.5;

const CHANGE_DURATION = 350;

const PLOT_TOP = 22;
const AXIS_HEIGHT = 34;

const formatAxisDate = (timestamp: number) => formatLocalDate("dd. LLL", timestamp);

interface SeriesRow {
    index: number;
    timestamp: number | null;
    compareTimestamp?: number | null;
}

export interface SeriesColors {
    series: string;
    compare: string;
    target: string;
}

type SeriesTarget = {
    value: number;
    label: string;
    lowerIsBetter?: boolean;
    /**
     * The figure the verdict is about, which is the one the card states in its headline. Without it a
     * "whole" verdict falls back to the last plotted point, and a period that missed its target all
     * along reads green because its final bucket came good - a card and its own chart disagreeing.
     */
    actual?: number | null;
} & ({verdict: "whole"} | {verdict: "split"; aboveLabel: string; belowLabel: string});

export interface SeriesSpec<Row extends SeriesRow> {
    rows: Row[];
    valueKey: keyof Row & string;
    compareKey?: keyof Row & string;
    label: string;
    compareLabel?: string;
    target?: SeriesTarget;
    /**
     * A row field holding this bucket's own target, for a budget that moves as the period runs — a
     * running total climbs, so it is drawn as a line rather than the flat rule a fixed target gets.
     * The series still takes its verdict colour from where it ended up against its own last target;
     * only the split fill is off, because that needs one horizontal line to split the gradient on.
     */
    targetKey?: keyof Row & string;
}

interface ResolvedSeries<Row extends SeriesRow> {
    paint: Paint;
    belowPaint: Paint | null;
    compareColor: string;
    pointColor: (row: Row) => string;
}

function useResolvedSeries<Row extends SeriesRow>({rows, valueKey, target, targetKey}: SeriesSpec<Row>): ResolvedSeries<Row> {
    const theme = useTheme();

    const neutral = React.useMemo<Paint>(
        () => ({
            stroke: theme.palette.primary.main,
            strokeHover: darken(theme.palette.primary.main, 0.25),
            fill: lighten(theme.palette.primary.main, 0.72)
        }),
        [theme.palette.primary.main]
    );

    const meets = (value: number) =>
        target != null && (target.lowerIsBetter ? value <= target.value : value >= target.value);

    const stated = target?.actual;
    const latest = stated != null ? stated : [...rows].reverse().find(row => row[valueKey] != null)?.[valueKey];
    const meetsTarget = typeof latest === "number" && meets(latest);

    const abovePaint = target?.lowerIsBetter ? NEGATIVE_PAINT : POSITIVE_PAINT;
    const belowPaint = target?.lowerIsBetter ? POSITIVE_PAINT : NEGATIVE_PAINT;

    const canSplit = target?.verdict === "split" && targetKey == null;

    const paint = target == null
        ? neutral
        : canSplit
            ? abovePaint
            : meetsTarget ? POSITIVE_PAINT : NEGATIVE_PAINT;

    return {
        paint,
        belowPaint: canSplit ? belowPaint : null,
        compareColor: theme.palette.text.secondary,
        pointColor: row => {
            if (!canSplit) return paint.stroke;

            const value = row[valueKey];
            return typeof value === "number" && meets(value) ? POSITIVE_COLOR : NEGATIVE_COLOR;
        }
    };
}

export function InsightsSeriesLegend<Row extends SeriesRow>(spec: SeriesSpec<Row>) {
    const {label, compareKey, compareLabel, target, targetKey} = spec;
    const {paint, belowPaint, compareColor} = useResolvedSeries(spec);

    const split = target?.verdict === "split" && targetKey == null && belowPaint != null;
    const entries = (split ? 2 : 1)
        + (compareKey != null && compareLabel != null ? 1 : 0)
        + (target != null ? 1 : 0);

    // A single entry would name the only line on the chart, which the card's title already did.
    if (entries < 2) {
        return null;
    }

    return (
        <InsightsLegend>
            {split ? (
                <React.Fragment>
                    <LegendEntry color={paint.stroke} label={target.aboveLabel} width={SERIES_WIDTH}/>
                    <LegendEntry color={belowPaint.stroke} label={target.belowLabel} width={SERIES_WIDTH}/>
                </React.Fragment>
            ) : (
                <LegendEntry color={paint.stroke} label={label} width={SERIES_WIDTH}/>
            )}

            {compareKey != null && compareLabel != null && (
                <LegendEntry dashed color={compareColor} label={compareLabel} width={COMPARE_WIDTH}/>
            )}

            {target != null && <LegendEntry color={TARGET_COLOR} label={target.label} width={TARGET_WIDTH}/>}
        </InsightsLegend>
    );
}

interface AxisLabel {
    text: string;
    compareOnly: boolean;
}

interface AxisTickProps {
    x?: number;
    y?: number;
    payload?: {value?: number};

    firstIndex: number;
    lastIndex: number;
    labelFor: (index: number) => AxisLabel | null;
    color: string;
    compareColor: string;
}

function AxisTick({x, y, payload, firstIndex, lastIndex, labelFor, color, compareColor}: AxisTickProps) {
    const index = payload?.value;
    const label = index == null ? null : labelFor(index);

    if (x == null || y == null || index == null || label == null) {
        return null;
    }

    const anchor = index === firstIndex ? "start" : index === lastIndex ? "end" : "middle";

    return (
        <text
            x={x}
            y={y}
            textAnchor={anchor}
            dominantBaseline={"hanging"}
            fontSize={11}
            fill={label.compareOnly ? compareColor : color}
        >
            {label.text}
        </text>
    );
}

interface ActiveDotProps<Row> {
    cx?: number;
    cy?: number;
    payload?: Row;
    color?: string;
    colorFor?: (row: Row) => string;
    ringColor: string;
    r?: number;
}

interface SeriesDotProps<Row> {
    cx?: number;
    cy?: number;
    payload?: Row;
    colorFor: (row: Row) => string;
    ringColor: string;
    r: number;
}

/** A day that carries a value gets its own mark: a lone point has no line to be read off. */
function SeriesDot<Row>({cx, cy, payload, colorFor, ringColor, r}: SeriesDotProps<Row>) {
    // A day without a value still reaches here, with no place on the plot to be drawn at.
    if (payload == null || !Number.isFinite(cx) || !Number.isFinite(cy)) {
        return null;
    }

    return <circle cx={cx} cy={cy} r={r} fill={colorFor(payload)} stroke={ringColor} strokeWidth={1.5}/>;
}

function ActiveDot<Row>({cx, cy, payload, color, colorFor, ringColor, r = 4.5}: ActiveDotProps<Row>) {
    if (cx == null || cy == null) {
        return null;
    }

    const fill = colorFor != null && payload != null ? colorFor(payload) : color;

    return (
        <g pointerEvents={"none"}>
            <circle cx={cx} cy={cy} r={r * 2} fill={fill} fillOpacity={0.18}/>
            <circle cx={cx} cy={cy} r={r} fill={fill} stroke={ringColor} strokeWidth={2}/>
        </g>
    );
}

interface SeriesTooltipProps<Row extends SeriesRow> {
    active?: boolean;
    payload?: Array<{payload?: Row}>;
    compareLabel?: string;
    rows: (row: Row) => React.ReactNode;
}

function SeriesTooltip<Row extends SeriesRow>({active, payload, compareLabel, rows}: SeriesTooltipProps<Row>) {
    const row = payload?.[0]?.payload;

    if (!active || row == null) {
        return null;
    }

    const day = row.timestamp ?? row.compareTimestamp;

    return (
        <InsightsTooltipPanel>
            <TooltipTitle
                title={day == null ? "" : formatFullDate(day)}
                subtitle={row.timestamp == null ? compareLabel : undefined}
            />
            {rows(row)}
        </InsightsTooltipPanel>
    );
}

interface ChartProps<Row extends SeriesRow> extends SeriesSpec<Row> {
    height: number;
    domain: [number, number];
    ticks?: number[];
    formatTick: (value: number) => string;
    tooltip: (row: Row, colors: SeriesColors) => React.ReactNode;
    empty?: React.ReactNode;
    /** Bridges the days the series cannot be computed for, instead of breaking the line there. */
    connectNulls?: boolean;
    /**
     * A row field holding the foot of the plot on the days the series has no value. Drawn as a
     * dashed rule, so the eye is led along the quiet stretch to the day that does carry a point.
     */
    baselineKey?: keyof Row & string;
    /**
     * A bucket to call out on the axis — the one the period is still inside, whose figure is therefore
     * partial. A dashed rule, so it reads as a boundary rather than as another series.
     */
    marker?: {index: number; label: string};
}

/** Above this many real points the line carries itself and the marks only need to be findable. */
const SPARSE_POINTS = 3;
const DOT_RADIUS = 3;
const SPARSE_DOT_RADIUS = 5;

export function InsightsSeriesChart<Row extends SeriesRow>(props: ChartProps<Row>) {
    const {
        rows,
        height,
        valueKey,
        compareKey,
        compareLabel,
        domain,
        ticks,
        formatTick,
        target,
        targetKey,
        tooltip,
        empty,
        connectNulls,
        baselineKey,
        marker
    } = props;
    const {paint, belowPaint, compareColor, pointColor} = useResolvedSeries(props);
    const theme = useTheme();

    const labelFor = React.useCallback(
        (index: number): AxisLabel | null => {
            const row = rows[index];
            if (row == null) return null;

            if (row.timestamp != null) return {text: formatAxisDate(row.timestamp), compareOnly: false};
            if (row.compareTimestamp != null) return {text: formatAxisDate(row.compareTimestamp), compareOnly: true};

            return null;
        },
        [rows]
    );

    const revealedRef = React.useRef(false);
    const animationDuration = revealedRef.current ? CHANGE_DURATION : BAR_DRAW_DURATION;

    React.useEffect(() => {
        revealedRef.current = true;
    }, []);

    const reactId = React.useId();
    const gradientBase = React.useMemo(() => `series-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`, [reactId]);
    const fillId = `${gradientBase}-fill`;
    const strokeId = `${gradientBase}-stroke`;
    const strokeHoverId = `${gradientBase}-stroke-hover`;

    const dotRingColor = theme.palette.background.paper;

    const pointCount = rows.reduce((count, row) => (row[valueKey] == null ? count : count + 1), 0);
    const dotRadius = pointCount <= SPARSE_POINTS ? SPARSE_DOT_RADIUS : DOT_RADIUS;

    const [yMin, yMax] = domain;
    const targetOffset = target == null || targetKey != null || yMax <= yMin
        ? null
        : Math.min(1, Math.max(0, 1 - (target.value - yMin) / (yMax - yMin)));

    const split = targetOffset != null && belowPaint != null ? {paint: belowPaint, offset: targetOffset} : null;

    const plotTop = PLOT_TOP;
    const plotBottom = height - AXIS_HEIGHT;

    const strokePaint = split == null ? paint.stroke : `url(#${strokeId})`;
    const strokeHoverPaint = split == null ? paint.strokeHover : `url(#${strokeHoverId})`;

    const tooltipRows = (row: Row) =>
        tooltip(row, {series: pointColor(row), compare: compareColor, target: TARGET_COLOR});

    return (
        <Box
            sx={{
                width: "100%",
                minWidth: 0,
                height,
                "@keyframes insightsTargetFadeIn": {from: {opacity: 0}, to: {opacity: 1}},
                "& .recharts-reference-line": {
                    animation: `insightsTargetFadeIn ${MARKER_FADE_DURATION}ms ease-out ${MARKER_FADE_DELAY}ms both`
                },
                "& .recharts-area-curve": {
                    stroke: strokePaint,
                    strokeWidth: SERIES_WIDTH,
                    transition: "stroke 220ms ease, stroke-width 220ms ease"
                },
                "&:hover .recharts-area-curve": {
                    stroke: strokeHoverPaint,
                    strokeWidth: SERIES_WIDTH_HOVER
                }
            }}
        >
            {rows.length === 0 ? (
                <Box sx={{height: "100%", display: "grid", placeItems: "center"}}>{empty}</Box>
            ) : (
                <ResponsiveContainer width={"100%"} height={"100%"} minWidth={0}>
                    <AreaChart
                        data={rows}
                        margin={{top: plotTop, right: 8, bottom: 0, left: 0}}
                        accessibilityLayer={false}
                    >
                        <defs>
                            {split == null ? (
                                <linearGradient id={fillId} x1={"0"} y1={"0"} x2={"0"} y2={"1"}>
                                    <stop offset={"0%"} stopColor={paint.fill} stopOpacity={0.85}/>
                                    <stop offset={"55%"} stopColor={paint.fill} stopOpacity={0.3}/>
                                    <stop offset={"100%"} stopColor={paint.fill} stopOpacity={0}/>
                                </linearGradient>
                            ) : (
                                <React.Fragment>
                                    <linearGradient
                                        id={fillId}
                                        gradientUnits={"userSpaceOnUse"}
                                        x1={0}
                                        y1={plotTop}
                                        x2={0}
                                        y2={plotBottom}
                                    >
                                        <stop offset={0} stopColor={paint.fill} stopOpacity={0.85}/>
                                        <stop offset={split.offset} stopColor={paint.fill} stopOpacity={0.32}/>
                                        <stop offset={split.offset} stopColor={split.paint.fill} stopOpacity={0.32}/>
                                        <stop offset={1} stopColor={split.paint.fill} stopOpacity={0}/>
                                    </linearGradient>

                                    <linearGradient
                                        id={strokeId}
                                        gradientUnits={"userSpaceOnUse"}
                                        x1={0}
                                        y1={plotTop}
                                        x2={0}
                                        y2={plotBottom}
                                    >
                                        <stop offset={split.offset} stopColor={paint.stroke}/>
                                        <stop offset={split.offset} stopColor={split.paint.stroke}/>
                                    </linearGradient>

                                    <linearGradient
                                        id={strokeHoverId}
                                        gradientUnits={"userSpaceOnUse"}
                                        x1={0}
                                        y1={plotTop}
                                        x2={0}
                                        y2={plotBottom}
                                    >
                                        <stop offset={split.offset} stopColor={paint.strokeHover}/>
                                        <stop offset={split.offset} stopColor={split.paint.strokeHover}/>
                                    </linearGradient>
                                </React.Fragment>
                            )}
                        </defs>

                        <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeOpacity={0.8}/>

                        <XAxis
                            dataKey={"index"}
                            type={"number"}
                            domain={["dataMin", "dataMax"]}
                            allowDecimals={false}
                            axisLine={false}
                            tickLine={false}
                            minTickGap={160}
                            tickMargin={12}
                            height={AXIS_HEIGHT}
                            tick={
                                <AxisTick
                                    firstIndex={0}
                                    lastIndex={rows.length - 1}
                                    labelFor={labelFor}
                                    color={theme.palette.text.secondary}
                                    compareColor={theme.palette.text.disabled}
                                />
                            }
                        />

                        <YAxis
                            width={40}
                            axisLine={false}
                            tickLine={false}
                            tickMargin={8}
                            domain={domain}
                            ticks={ticks}
                            allowDecimals={false}
                            tick={{fill: theme.palette.text.secondary, fontSize: 11}}
                            tickFormatter={value => formatTick(Number(value))}
                        />

                        <RechartsTooltip
                            isAnimationActive={false}
                            offset={14}
                            cursor={{stroke: theme.palette.text.primary, strokeWidth: 1, strokeOpacity: 0.25}}
                            wrapperStyle={{pointerEvents: "none", transition: "none", outline: "none", zIndex: 10}}
                            content={<SeriesTooltip<Row> compareLabel={compareLabel} rows={tooltipRows}/>}
                        />

                        {target != null && targetKey == null && (
                            <ReferenceLine
                                y={target.value}
                                stroke={TARGET_COLOR}
                                strokeWidth={TARGET_WIDTH}
                                strokeOpacity={0.8}
                            />
                        )}

                        {marker != null && (
                            <ReferenceLine
                                x={marker.index}
                                stroke={theme.palette.primary.main}
                                strokeWidth={1}
                                strokeOpacity={0.38}
                                strokeDasharray={"4 4"}
                                label={(
                                    <Label
                                        value={marker.label}
                                        position={"insideTopLeft"}
                                        fill={theme.palette.primary.main}
                                        fontSize={10}
                                        fontWeight={600}
                                    />
                                )}
                            />
                        )}

                        {targetKey != null && (
                            <Line
                                type={"linear"}
                                dataKey={targetKey}
                                stroke={TARGET_COLOR}
                                strokeWidth={TARGET_WIDTH}
                                strokeOpacity={0.8}
                                dot={false}
                                activeDot={false}
                                connectNulls
                                isAnimationActive={false}
                                legendType={"none"}
                            />
                        )}

                        {baselineKey != null && (
                            <Line
                                type={"linear"}
                                dataKey={baselineKey}
                                stroke={theme.palette.divider}
                                strokeWidth={1.5}
                                strokeDasharray={"4 4"}
                                strokeOpacity={0.8}
                                dot={false}
                                activeDot={false}
                                isAnimationActive={false}
                                legendType={"none"}
                            />
                        )}

                        {compareKey != null && (
                            <Line
                                type={"linear"}
                                dataKey={compareKey}
                                stroke={compareColor}
                                strokeWidth={COMPARE_WIDTH}
                                strokeDasharray={"5 4"}
                                strokeLinecap={"round"}
                                dot={false}
                                activeDot={<ActiveDot<Row> r={3.5} color={compareColor} ringColor={dotRingColor}/>}
                                animationDuration={animationDuration}
                                animationEasing={"ease-out"}
                            />
                        )}

                        <Area
                            type={"linear"}
                            dataKey={valueKey}
                            stroke={paint.stroke}
                            strokeLinecap={"round"}
                            strokeLinejoin={"round"}
                            fill={`url(#${fillId})`}
                            fillOpacity={1}
                            connectNulls={connectNulls}
                            // Dots only where they carry the chart: a sparse range cannot draw a line between
                            // three points, so they mark the days; a dense range shows the hovered day alone.
                            dot={pointCount <= SPARSE_POINTS
                                ? <SeriesDot<Row> colorFor={pointColor} ringColor={dotRingColor} r={dotRadius}/>
                                : false}
                            activeDot={<ActiveDot<Row> colorFor={pointColor} ringColor={dotRingColor}/>}
                            animationDuration={animationDuration}
                            animationEasing={"ease-out"}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </Box>
    );
}
